import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Download, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

export const Books = () => {
  const [books, setBooks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/books`);
      console.log('Books loaded:', response.data);
      setBooks(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Error loading books:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-xl">Loading books...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">Error: {error}</p>
          <Button onClick={loadBooks} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }

  const categories = ['all', ...new Set(books.map(b => b.category))];
  const filteredBooks = filter === 'all' ? books : books.filter(b => b.category === filter);

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-20 w-20 mx-auto mb-6 text-purple-200" />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6" style={{fontFamily: 'Work Sans'}}>
            Nigerland Books
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
            Transformative literature on governance, development, values, and nation-building
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex flex-wrap gap-3 justify-center">
          {categories.map(cat => (
            <Button
              key={cat}
              onClick={() => setFilter(cat)}
              variant={filter === cat ? 'default' : 'outline'}
              className="capitalize"
            >
              {cat === 'all' ? 'All Books' : cat}
            </Button>
          ))}
        </div>

        {filteredBooks.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">No books found in this category.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-2">{filter === 'all' ? 'All Publications' : filter}</h2>
              <p className="text-gray-600">{filteredBooks.length} book(s) available</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {filteredBooks.map(book => (
                <Card key={book.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden">
                      <img 
                        src={book.cover_image} 
                        alt={book.title}
                        className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/books/placeholder.jpg';
                        }}
                      />
                      {book.pdf_filename && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-green-500 text-white flex items-center gap-1">
                            <Download className="h-3 w-3" />PDF
                          </Badge>
                        </div>
                      )}
                      <Badge className="absolute top-3 left-3">{book.category}</Badge>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-purple-700 transition">{book.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">by {book.author}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-purple-700">₦{book.price.toLocaleString()}</span>
                        <Link to={`/books/${book.id}`}>
                          <Button size="sm" className="group-hover:bg-purple-700">View</Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      <section className="bg-gradient-to-r from-purple-100 to-blue-100 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">About Our Publications</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Nigerland Books, led by author <strong>Kelechi Ngwaba (FCA)</strong>, publishes thought-provoking literature addressing governance, development, and societal values. Our books have been distributed to over 60 tertiary institutions and key organizations across Nigeria, contributing to national discourse and educational advancement.
          </p>
        </div>
      </section>
    </div>
  );
};